/**
 * This is where the magic all happens. Well, managing state anyway.
 *
 * The data structures we need to manage are hierachical in nature.
 *
 * There are essentially three different Value types,
 *
 *   - Simple or Primitive Type (e.g. String, Boolean, Password, Number, Datetime, ...), a single property.
 *   - Complex Type (ContentObject) an item with a number of properties, each property can be
 *     of any Type. Basically this is a Javascript Object.
 *   - List Type (ContentList) essentially an array of the same Complex Types. E.g. [{}, {}, {}]
 *
 * Types can be nested indefinitely creating a tree like hierarichal structure.
 *
 * When creating or editing our Value, there is the option to store default values against zero or
 * more properties. Below is an example of a primitive Value, 'MyValue' of Type String with a default
 * value of 'My Default String'. In addition we can store user-defined formatting masks applied during
 * display of the value, e.g. 'DD/MM/YY', or '$99.99' for a Number.
 *
 * {
        access: "PRIVATE"
        contentFormat: ""
        contentType: "ContentString"
        dateCreated: "2019-09-26T10:07:33.2290244+00:00"
        dateModified: "2019-10-01T12:28:39.981267+00:00"
        defaultContentValue: "My Default String"
        defaultObjectData: null
        developerName: "MyValue"
        developerSummary: ""
        elementType: "VARIABLE"
        id: "3fa1f789-c916-4ee0-857a-bd878fcce865"
        initializationOperations: null
        isFixed: false
        isVersionless: false
        typeElementDeveloperName: null
        typeElementId: null
        updateByName: false
        whoCreated: {email: "john.doe@dell.com", firstName: "John", id: "6c6e8404-c255-461b-9fe1-f6a6c4f1fbb0",…}
        whoModified: {email: "john.doe@dell.com", firstName: "John", id: "6c6e8404-c255-461b-9fe1-f6a6c4f1fbb0",…}
        whoOwner: {email: "john.doe@dell.com", firstName: "John", id: "6c6e8404-c255-461b-9fe1-f6a6c4f1fbb0",…}
 * }
 *
 * Note: This object shows the timestamp and 'auditing' details (who* properties) that are managed by the API. They are
 * not shown in following examples for clarity.
 *
 * A Primitive Value is straight forward, no nesting and a single top-level property, defaultContentValue.
 *
 *
 * For a Value with an Object or List type we need a Type definition to determine the names, default values, formatting
 * and Type of each of the object's properties.
 *
 * A Type has the following basic structure,
 *
 * {
        bindings: null
        developerName: "My Type"
        developerSummary: ""
        elementType: "TYPE"
        id: "8552c8a7-78dd-468c-83f9-aecc8f7f86b9"
        properties: [
            0: {contentFormat: ""
                contentType: "ContentBoolean"
                developerName: "My Boolean"
                id: "96bb79e3-034f-4495-9991-7663fab9bb12"
                typeElementDeveloperName: null
                typeElementId: null}
            1: {contentFormat: ""
                contentType: "ContentContent"
                developerName: "My HTML content"
                id: "ef0ce3ff-6028-49a4-83cf-57b264f93fde"
                typeElementDeveloperName: null
                typeElementId: null}
            2: {contentFormat: ""
                contentType: "ContentString"
                developerName: "My String"
                id: "a6df4e5e-47ea-49e5-a4bc-cd4824b6256d"
                typeElementDeveloperName: null
                typeElementId: null}
        ]
        serviceElementDeveloperName: null
        serviceElementId: null
        updateByName: false
 * }
 *
 * This describes a Type, 'My Type' with three properties, Boolean, Content & String.
 *
 * Putting this all together we can describe an Object Value as follows,
 *
 * {
    access: "PRIVATE"
        contentFormat: ""
        contentType: "ContentObject"
        defaultContentValue: null
        defaultObjectData: [
            0: {
                developerName: "My Type"
                isSelected: false
                order: 0
                properties: [,…]
                    0: {
                    contentFormat: ""
                    contentType: "ContentBoolean"
                    contentValue: "true"
                    developerName: "My Boolean"
                    objectData: null
                    typeElementId: null
                    typeElementPropertyId: "96bb79e3-034f-4495-9991-7663fab9bb12"
                    1: {contentFormat: "", contentType: "ContentContent", contentValue: "Default content", developerName: "My HTML content",…}
                    2: {contentFormat: "", contentType: "ContentString", contentValue: "Default string", developerName: "My String",…}
                typeElementBindingDeveloperName: null
                typeElementId: "8552c8a7-78dd-468c-83f9-aecc8f7f86b9"
            }
        ]
        developerName: "My Object Value"
        developerSummary: ""
        elementType: "VARIABLE"
        id: "068a3bf9-b57e-4f1f-85d3-5d3ab2e061f2"
        initializationOperations: null
        isFixed: false
        isVersionless: false
        typeElementDeveloperName: "My Type"
        typeElementId: "8552c8a7-78dd-468c-83f9-aecc8f7f86b9"
        updateByName: false
 * }
 *
 * Any default data required for this Value Object is no longer stored in defaultContentValue, but in defaultObjectData.
 *
 * defaultObjectData is a array with ONE item, that describes default values for each property. This may be null if no default data is supplied.
 * Note that defaultObjectData[0].typeElementId references the Type definition required for the default data. typeElementId in the Value
 * describes the Type of this Value.
 *
 *
 * The definition of a List Value is very similar to an Object Value except that
 *
 *   1. The contentType would be "ContentList"
 *   2. The defaultObjectData properties array may contain more than one row, one each for the default values of each list item.
 *
 * Time to mention a small *gotcha*. Type definitions can be changed after a Value is created with default data. This means the 'properties'
 * array above may not have all the properties listed in the Type definition, i.e. the Type could have properties, added, removed, renamed or
 * even changed type retaining the same name. This needs to be managed here.
 *
 * Also, to add more trouble, a Value may be modified and the Types of each property can be changed, meaning the existing default data is
 * probably invalid, and we can change property names of existing types !
 *
 * Hold on - we are not finished yet - now it starts to get complicated.
 *
 * Values can have multiple levels of data structures with optional default data at each level. For example we could create a Object Value
 * with a Type that has a child Type that may be a List. Or a List of Values with one of the List properties being a Complex Type. This means
 * a Value may have children, grandchildren, great-grandchildren, ... etc. in a tree like heirarchical fashion.
 *
 * To illustrate, using a slightly convoluted example, we are creating a four level deep hierarchy of default data for ficticious
 * conference schedules. The structure demostrates Lists, Objects, Lists of Objects and Objects containing Lists.
 *
 * The Types for the example data below are structured thus:
 *
 * Person (ContentObject) : {                           A Person
 *      First (ContentString) : ""
 *      Last (ContentString) : ""
 * }
 *
 * Subject (ContentObject) : {                          A lecture to be presented at a Conference
 *      Name (ContentString) : ""
 *      Description (ContentString) : ""
 *  }
 *
 * Presenter (ContentObject) : {                        The name of the Presenter and Subjects to be presented
 *      Person (ContentObject) : Person
 *      Subjects (ContentList) : [Subject]
 * }
 *
 * Conference (ContentObject) : {
 *      Place (ContentString) : ""
 *      Attendees (ContentList) : [Person]              Conference attendees
 *      Presenter (ContentObject) : {Presenter}
 * }
 *
 *
 * Schedule (ContentObject) : {
 *      When (ContentDateTime) : ""
 *      Conference (ContentList) : [Conference]         A list of conferences for this schedule
 *      Fully Booked (ContentBoolean) : true/false
 * }
 *
 *
 * Then we create a new Value 'Training' as a List of Schedule, i.e. a calendar of conferences. The example Value has
 * two Schedules, each with a number of Conferences, with one Presenter delivering a number of lectures on various Subjects to a
 * collection of Attendees
 *
 * Training (ContentList) : [Schedule]
 *
 * Brace yourself, below is the Javascript object for Training with a 'fully populated' set of default values.
 *
 *
    {
        "access": "PRIVATE",
        "contentFormat": "",
        "contentType": "ContentList",
        "dateCreated": "2019-10-04T11:21:12.9934544+00:00",
        "dateModified": "2019-10-04T12:33:37.1584819+00:00",
        "defaultContentValue": null,
        "defaultObjectData": [
            {
            "developerName": "Schedule",
            "externalId": "078a67dd-429d-4451-9d30-927d5c1fa907",
            "internalId": "078a67dd-429d-4451-9d30-927d5c1fa907",
            "isSelected": false,
            "order": 0,
            "properties": [
                {
                "contentFormat": "",
                "contentType": "ContentList",
                "contentValue": null,
                "developerName": "Conference",
                "objectData": [
                    {
                    "developerName": "Conference",
                    "externalId": "143625be-8925-426a-95c3-a3ec96c8d74b",
                    "internalId": "143625be-8925-426a-95c3-a3ec96c8d74b",
                    "isSelected": false,
                    "order": 0,
                    "properties": [
                        {
                        "contentFormat": "",
                        "contentType": "ContentList",
                        "contentValue": null,
                        "developerName": "Attendees",
                        "objectData": [
                            {
                            "developerName": "Person",
                            "externalId": "0eff88d4-1030-45e8-82fe-ddd565fa11b9",
                            "internalId": "0eff88d4-1030-45e8-82fe-ddd565fa11b9",
                            "isSelected": false,
                            "order": 0,
                            "properties": [
                                {
                                "contentFormat": "",
                                "contentType": "ContentString",
                                "contentValue": "Attendee",
                                "developerName": "First Name",
                                "objectData": null,
                                "typeElementId": null,
                                "typeElementPropertyId": "ce20f295-5049-446a-868f-494d8012fe4d"
                                },
                                {
                                "contentFormat": "",
                                "contentType": "ContentString",
                                "contentValue": "One",
                                "developerName": "Last Name",
                                "objectData": null,
                                "typeElementId": null,
                                "typeElementPropertyId": "39404b1f-0675-4347-9578-bb27cb319c61"
                                }
                            ],
                            "typeElementBindingDeveloperName": null,
                            "typeElementId": "196f8e20-806f-4c96-84b9-694ce2f2b94a"
                            },
                            {
                            "developerName": "Person",
                            "externalId": "03ac2100-d819-4174-816e-595ef8761079",
                            "internalId": "03ac2100-d819-4174-816e-595ef8761079",
                            "isSelected": false,
                            "order": 1,
                            "properties": [
                                {
                                "contentFormat": "",
                                "contentType": "ContentString",
                                "contentValue": "Attendee",
                                "developerName": "First Name",
                                "objectData": null,
                                "typeElementId": null,
                                "typeElementPropertyId": "ce20f295-5049-446a-868f-494d8012fe4d"
                                },
                                {
                                "contentFormat": "",
                                "contentType": "ContentString",
                                "contentValue": "Two",
                                "developerName": "Last Name",
                                "objectData": null,
                                "typeElementId": null,
                                "typeElementPropertyId": "39404b1f-0675-4347-9578-bb27cb319c61"
                                }
                            ],
                            "typeElementBindingDeveloperName": null,
                            "typeElementId": "196f8e20-806f-4c96-84b9-694ce2f2b94a"
                            },
                            {
                            "developerName": "Person",
                            "externalId": "54825bc3-c430-41a2-8377-35b8c5bccd8d",
                            "internalId": "54825bc3-c430-41a2-8377-35b8c5bccd8d",
                            "isSelected": false,
                            "order": 2,
                            "properties": [
                                {
                                "contentFormat": "",
                                "contentType": "ContentString",
                                "contentValue": "Attendee",
                                "developerName": "First Name",
                                "objectData": null,
                                "typeElementId": null,
                                "typeElementPropertyId": "ce20f295-5049-446a-868f-494d8012fe4d"
                                },
                                {
                                "contentFormat": "",
                                "contentType": "ContentString",
                                "contentValue": "Three",
                                "developerName": "Last Name",
                                "objectData": null,
                                "typeElementId": null,
                                "typeElementPropertyId": "39404b1f-0675-4347-9578-bb27cb319c61"
                                }
                            ],
                            "typeElementBindingDeveloperName": null,
                            "typeElementId": "196f8e20-806f-4c96-84b9-694ce2f2b94a"
                            }
                        ],
                        "typeElementId": "196f8e20-806f-4c96-84b9-694ce2f2b94a",
                        "typeElementPropertyId": "247ffcdd-1f35-416a-86da-b8297c9d4d17"
                        },
                        {
                        "contentFormat": "",
                        "contentType": "ContentString",
                        "contentValue": "Exeter",
                        "developerName": "Place",
                        "objectData": null,
                        "typeElementId": null,
                        "typeElementPropertyId": "c0570c38-e55f-4165-a37f-a95ae91a7f4b"
                        },
                        {
                        "contentFormat": "",
                        "contentType": "ContentObject",
                        "contentValue": null,
                        "developerName": "Presenter",
                        "objectData": [
                            {
                            "developerName": "Presenter",
                            "externalId": "269cfb25-4f80-4caf-bf3f-17e38d20fa53",
                            "internalId": "269cfb25-4f80-4caf-bf3f-17e38d20fa53",
                            "isSelected": false,
                            "order": 0,
                            "properties": [
                                {
                                "contentFormat": "",
                                "contentType": "ContentObject",
                                "contentValue": null,
                                "developerName": "Presenter",
                                "objectData": [
                                    {
                                    "developerName": "Person",
                                    "externalId": "4854c500-3949-49fe-9d09-09886da91824",
                                    "internalId": "4854c500-3949-49fe-9d09-09886da91824",
                                    "isSelected": false,
                                    "order": 0,
                                    "properties": [
                                        {
                                        "contentFormat": "",
                                        "contentType": "ContentString",
                                        "contentValue": "Joe",
                                        "developerName": "First Name",
                                        "objectData": null,
                                        "typeElementId": null,
                                        "typeElementPropertyId": "ce20f295-5049-446a-868f-494d8012fe4d"
                                        },
                                        {
                                        "contentFormat": "",
                                        "contentType": "ContentString",
                                        "contentValue": "Trainer",
                                        "developerName": "Last Name",
                                        "objectData": null,
                                        "typeElementId": null,
                                        "typeElementPropertyId": "39404b1f-0675-4347-9578-bb27cb319c61"
                                        }
                                    ],
                                    "typeElementBindingDeveloperName": null,
                                    "typeElementId": "196f8e20-806f-4c96-84b9-694ce2f2b94a"
                                    }
                                ],
                                "typeElementId": "196f8e20-806f-4c96-84b9-694ce2f2b94a",
                                "typeElementPropertyId": "a9dde048-bad9-4709-b573-09562d1b5d67"
                                },
                                {
                                "contentFormat": "",
                                "contentType": "ContentList",
                                "contentValue": null,
                                "developerName": "Subjects",
                                "objectData": [
                                    {
                                    "developerName": "Subject",
                                    "externalId": "420ea94c-dccd-409f-8100-bc4b068dd800",
                                    "internalId": "420ea94c-dccd-409f-8100-bc4b068dd800",
                                    "isSelected": false,
                                    "order": 0,
                                    "properties": [
                                        {
                                        "contentFormat": "",
                                        "contentType": "ContentString",
                                        "contentValue": "Pure and applied",
                                        "developerName": "Description",
                                        "objectData": null,
                                        "typeElementId": null,
                                        "typeElementPropertyId": "efda076a-c99f-4ff5-921a-44c866f6e0bc"
                                        },
                                        {
                                        "contentFormat": "",
                                        "contentType": "ContentString",
                                        "contentValue": "Maths",
                                        "developerName": "Name",
                                        "objectData": null,
                                        "typeElementId": null,
                                        "typeElementPropertyId": "1bb3a134-ba03-4d5d-b9b9-f12584156c94"
                                        }
                                    ],
                                    "typeElementBindingDeveloperName": null,
                                    "typeElementId": "69eaa80d-5cf6-4287-bdcd-a9f31e6eb0db"
                                    },
                                    {
                                    "developerName": "Subject",
                                    "externalId": "71daa5c1-2ee0-48e3-9fcd-f4e478457167",
                                    "internalId": "71daa5c1-2ee0-48e3-9fcd-f4e478457167",
                                    "isSelected": false,
                                    "order": 1,
                                    "properties": [
                                        {
                                        "contentFormat": "",
                                        "contentType": "ContentString",
                                        "contentValue": "",
                                        "developerName": "Description",
                                        "objectData": null,
                                        "typeElementId": null,
                                        "typeElementPropertyId": "efda076a-c99f-4ff5-921a-44c866f6e0bc"
                                        },
                                        {
                                        "contentFormat": "",
                                        "contentType": "ContentString",
                                        "contentValue": "English",
                                        "developerName": "Name",
                                        "objectData": null,
                                        "typeElementId": null,
                                        "typeElementPropertyId": "1bb3a134-ba03-4d5d-b9b9-f12584156c94"
                                        }
                                    ],
                                    "typeElementBindingDeveloperName": null,
                                    "typeElementId": "69eaa80d-5cf6-4287-bdcd-a9f31e6eb0db"
                                    },
                                    {
                                    "developerName": "Subject",
                                    "externalId": "5499f323-ed5d-47ac-b50e-6ff0130c653f",
                                    "internalId": "5499f323-ed5d-47ac-b50e-6ff0130c653f",
                                    "isSelected": false,
                                    "order": 2,
                                    "properties": [
                                        {
                                        "contentFormat": "",
                                        "contentType": "ContentString",
                                        "contentValue": "Banksy",
                                        "developerName": "Description",
                                        "objectData": null,
                                        "typeElementId": null,
                                        "typeElementPropertyId": "efda076a-c99f-4ff5-921a-44c866f6e0bc"
                                        },
                                        {
                                        "contentFormat": "",
                                        "contentType": "ContentString",
                                        "contentValue": "Art",
                                        "developerName": "Name",
                                        "objectData": null,
                                        "typeElementId": null,
                                        "typeElementPropertyId": "1bb3a134-ba03-4d5d-b9b9-f12584156c94"
                                        }
                                    ],
                                    "typeElementBindingDeveloperName": null,
                                    "typeElementId": "69eaa80d-5cf6-4287-bdcd-a9f31e6eb0db"
                                    }
                                ],
                                "typeElementId": "69eaa80d-5cf6-4287-bdcd-a9f31e6eb0db",
                                "typeElementPropertyId": "fdd284aa-2074-434a-8b2b-53b0cb5dd465"
                                }
                            ],
                            "typeElementBindingDeveloperName": null,
                            "typeElementId": "84155ccd-67b7-4e3d-be14-c9fa187aea93"
                            }
                        ],
                        "typeElementId": "84155ccd-67b7-4e3d-be14-c9fa187aea93",
                        "typeElementPropertyId": "1bdd553f-0ac7-4215-9494-b7c45b277f99"
                        }
                    ],
                    "typeElementBindingDeveloperName": null,
                    "typeElementId": "5c557d42-6720-4497-af84-9665d0f837aa"
                    },
                    {
                    "developerName": "Conference",
                    "externalId": "ff7dd1f5-d517-41a4-b576-ee88608aac33",
                    "internalId": "ff7dd1f5-d517-41a4-b576-ee88608aac33",
                    "isSelected": false,
                    "order": 1,
                    "properties": [
                        {
                        "contentFormat": "",
                        "contentType": "ContentList",
                        "contentValue": null,
                        "developerName": "Attendees",
                        "objectData": [
                            {
                            "developerName": "Person",
                            "externalId": "8a898624-c07f-4abe-b6bd-9070b756636c",
                            "internalId": "8a898624-c07f-4abe-b6bd-9070b756636c",
                            "isSelected": false,
                            "order": 0,
                            "properties": [
                                {
                                "contentFormat": "",
                                "contentType": "ContentString",
                                "contentValue": "Artist",
                                "developerName": "First Name",
                                "objectData": null,
                                "typeElementId": null,
                                "typeElementPropertyId": "ce20f295-5049-446a-868f-494d8012fe4d"
                                },
                                {
                                "contentFormat": "",
                                "contentType": "ContentString",
                                "contentValue": "One",
                                "developerName": "Last Name",
                                "objectData": null,
                                "typeElementId": null,
                                "typeElementPropertyId": "39404b1f-0675-4347-9578-bb27cb319c61"
                                }
                            ],
                            "typeElementBindingDeveloperName": null,
                            "typeElementId": "196f8e20-806f-4c96-84b9-694ce2f2b94a"
                            },
                            {
                            "developerName": "Person",
                            "externalId": "9b0c04d0-bac4-4dd8-b15a-330cc121be1f",
                            "internalId": "9b0c04d0-bac4-4dd8-b15a-330cc121be1f",
                            "isSelected": false,
                            "order": 1,
                            "properties": [
                                {
                                "contentFormat": "",
                                "contentType": "ContentString",
                                "contentValue": "Artist",
                                "developerName": "First Name",
                                "objectData": null,
                                "typeElementId": null,
                                "typeElementPropertyId": "ce20f295-5049-446a-868f-494d8012fe4d"
                                },
                                {
                                "contentFormat": "",
                                "contentType": "ContentString",
                                "contentValue": "Two",
                                "developerName": "Last Name",
                                "objectData": null,
                                "typeElementId": null,
                                "typeElementPropertyId": "39404b1f-0675-4347-9578-bb27cb319c61"
                                }
                            ],
                            "typeElementBindingDeveloperName": null,
                            "typeElementId": "196f8e20-806f-4c96-84b9-694ce2f2b94a"
                            }
                        ],
                        "typeElementId": "196f8e20-806f-4c96-84b9-694ce2f2b94a",
                        "typeElementPropertyId": "247ffcdd-1f35-416a-86da-b8297c9d4d17"
                        },
                        {
                        "contentFormat": "",
                        "contentType": "ContentString",
                        "contentValue": "London",
                        "developerName": "Place",
                        "objectData": null,
                        "typeElementId": null,
                        "typeElementPropertyId": "c0570c38-e55f-4165-a37f-a95ae91a7f4b"
                        },
                        {
                        "contentFormat": "",
                        "contentType": "ContentObject",
                        "contentValue": null,
                        "developerName": "Presenter",
                        "objectData": [
                            {
                            "developerName": "Presenter",
                            "externalId": "0a8fcd27-ebe7-483e-a4ad-2f50fa524204",
                            "internalId": "0a8fcd27-ebe7-483e-a4ad-2f50fa524204",
                            "isSelected": false,
                            "order": 0,
                            "properties": [
                                {
                                "contentFormat": "",
                                "contentType": "ContentObject",
                                "contentValue": null,
                                "developerName": "Presenter",
                                "objectData": [
                                    {
                                    "developerName": "Person",
                                    "externalId": "8ac3a775-32e3-4086-8d08-82e27f9317fe",
                                    "internalId": "8ac3a775-32e3-4086-8d08-82e27f9317fe",
                                    "isSelected": false,
                                    "order": 0,
                                    "properties": [
                                        {
                                        "contentFormat": "",
                                        "contentType": "ContentString",
                                        "contentValue": "Claude",
                                        "developerName": "First Name",
                                        "objectData": null,
                                        "typeElementId": null,
                                        "typeElementPropertyId": "ce20f295-5049-446a-868f-494d8012fe4d"
                                        },
                                        {
                                        "contentFormat": "",
                                        "contentType": "ContentString",
                                        "contentValue": "Monet",
                                        "developerName": "Last Name",
                                        "objectData": null,
                                        "typeElementId": null,
                                        "typeElementPropertyId": "39404b1f-0675-4347-9578-bb27cb319c61"
                                        }
                                    ],
                                    "typeElementBindingDeveloperName": null,
                                    "typeElementId": "196f8e20-806f-4c96-84b9-694ce2f2b94a"
                                    }
                                ],
                                "typeElementId": "196f8e20-806f-4c96-84b9-694ce2f2b94a",
                                "typeElementPropertyId": "a9dde048-bad9-4709-b573-09562d1b5d67"
                                },
                                {
                                "contentFormat": "",
                                "contentType": "ContentList",
                                "contentValue": null,
                                "developerName": "Subjects",
                                "objectData": [
                                    {
                                    "developerName": "Subject",
                                    "externalId": "a996add5-056b-4395-b3f0-248b636a0b13",
                                    "internalId": "a996add5-056b-4395-b3f0-248b636a0b13",
                                    "isSelected": false,
                                    "order": 0,
                                    "properties": [
                                        {
                                        "contentFormat": "",
                                        "contentType": "ContentString",
                                        "contentValue": "",
                                        "developerName": "Description",
                                        "objectData": null,
                                        "typeElementId": null,
                                        "typeElementPropertyId": "efda076a-c99f-4ff5-921a-44c866f6e0bc"
                                        },
                                        {
                                        "contentFormat": "",
                                        "contentType": "ContentString",
                                        "contentValue": "Fine Art",
                                        "developerName": "Name",
                                        "objectData": null,
                                        "typeElementId": null,
                                        "typeElementPropertyId": "1bb3a134-ba03-4d5d-b9b9-f12584156c94"
                                        }
                                    ],
                                    "typeElementBindingDeveloperName": null,
                                    "typeElementId": "69eaa80d-5cf6-4287-bdcd-a9f31e6eb0db"
                                    },
                                    {
                                    "developerName": "Subject",
                                    "externalId": "c90dfd4e-12a3-4fa2-aee6-0c8a7caa9dda",
                                    "internalId": "c90dfd4e-12a3-4fa2-aee6-0c8a7caa9dda",
                                    "isSelected": false,
                                    "order": 1,
                                    "properties": [
                                        {
                                        "contentFormat": "",
                                        "contentType": "ContentString",
                                        "contentValue": "",
                                        "developerName": "Description",
                                        "objectData": null,
                                        "typeElementId": null,
                                        "typeElementPropertyId": "efda076a-c99f-4ff5-921a-44c866f6e0bc"
                                        },
                                        {
                                        "contentFormat": "",
                                        "contentType": "ContentString",
                                        "contentValue": "Abstract Art",
                                        "developerName": "Name",
                                        "objectData": null,
                                        "typeElementId": null,
                                        "typeElementPropertyId": "1bb3a134-ba03-4d5d-b9b9-f12584156c94"
                                        }
                                    ],
                                    "typeElementBindingDeveloperName": null,
                                    "typeElementId": "69eaa80d-5cf6-4287-bdcd-a9f31e6eb0db"
                                    }
                                ],
                                "typeElementId": "69eaa80d-5cf6-4287-bdcd-a9f31e6eb0db",
                                "typeElementPropertyId": "fdd284aa-2074-434a-8b2b-53b0cb5dd465"
                                }
                            ],
                            "typeElementBindingDeveloperName": null,
                            "typeElementId": "84155ccd-67b7-4e3d-be14-c9fa187aea93"
                            }
                        ],
                        "typeElementId": "84155ccd-67b7-4e3d-be14-c9fa187aea93",
                        "typeElementPropertyId": "1bdd553f-0ac7-4215-9494-b7c45b277f99"
                        }
                    ],
                    "typeElementBindingDeveloperName": null,
                    "typeElementId": "5c557d42-6720-4497-af84-9665d0f837aa"
                    }
                ],
                "typeElementId": "5c557d42-6720-4497-af84-9665d0f837aa",
                "typeElementPropertyId": "1b5cbaf1-0cc9-4ad8-99ab-012ecee8a8fa"
                },
                {
                "contentFormat": "",
                "contentType": "ContentBoolean",
                "contentValue": "",
                "developerName": "Fully Booked",
                "objectData": null,
                "typeElementId": null,
                "typeElementPropertyId": "d7bd0d71-dd0a-4191-ac18-d21008211ad3"
                },
                {
                "contentFormat": "",
                "contentType": "ContentDateTime",
                "contentValue": "2019-10-09T12:17:00+01:00",
                "developerName": "When",
                "objectData": null,
                "typeElementId": null,
                "typeElementPropertyId": "1bd83bc6-15bb-4f35-8aba-e882dd300c72"
                }
            ],
            "typeElementBindingDeveloperName": null,
            "typeElementId": "f01facfb-a9bb-4404-90ca-b0861336ae4a"
            },
            {
            "developerName": "Schedule",
            "externalId": "a686446d-3928-47ae-b42b-b401c370b4f9",
            "internalId": "a686446d-3928-47ae-b42b-b401c370b4f9",
            "isSelected": false,
            "order": 1,
            "properties": [
                {
                "contentFormat": "",
                "contentType": "ContentList",
                "contentValue": null,
                "developerName": "Conference",
                "objectData": [
                    {
                    "developerName": "Conference",
                    "externalId": "2daef728-1d79-4953-8c58-19391ab86bf3",
                    "internalId": "2daef728-1d79-4953-8c58-19391ab86bf3",
                    "isSelected": false,
                    "order": 0,
                    "properties": [
                        {
                        "contentFormat": "",
                        "contentType": "ContentList",
                        "contentValue": null,
                        "developerName": "Attendees",
                        "objectData": [
                            {
                            "developerName": "Person",
                            "externalId": "8c0c7ad8-1758-47fe-ac07-ce4cb1b951e0",
                            "internalId": "8c0c7ad8-1758-47fe-ac07-ce4cb1b951e0",
                            "isSelected": false,
                            "order": 0,
                            "properties": [
                                {
                                "contentFormat": "",
                                "contentType": "ContentString",
                                "contentValue": "No",
                                "developerName": "First Name",
                                "objectData": null,
                                "typeElementId": null,
                                "typeElementPropertyId": "ce20f295-5049-446a-868f-494d8012fe4d"
                                },
                                {
                                "contentFormat": "",
                                "contentType": "ContentString",
                                "contentValue": "Show",
                                "developerName": "Last Name",
                                "objectData": null,
                                "typeElementId": null,
                                "typeElementPropertyId": "39404b1f-0675-4347-9578-bb27cb319c61"
                                }
                            ],
                            "typeElementBindingDeveloperName": null,
                            "typeElementId": "196f8e20-806f-4c96-84b9-694ce2f2b94a"
                            }
                        ],
                        "typeElementId": "196f8e20-806f-4c96-84b9-694ce2f2b94a",
                        "typeElementPropertyId": "247ffcdd-1f35-416a-86da-b8297c9d4d17"
                        },
                        {
                        "contentFormat": "",
                        "contentType": "ContentString",
                        "contentValue": "Nowhere",
                        "developerName": "Place",
                        "objectData": null,
                        "typeElementId": null,
                        "typeElementPropertyId": "c0570c38-e55f-4165-a37f-a95ae91a7f4b"
                        },
                        {
                        "contentFormat": "",
                        "contentType": "ContentObject",
                        "contentValue": null,
                        "developerName": "Presenter",
                        "objectData": [
                            {
                            "developerName": "Presenter",
                            "externalId": "f7c53798-dcfa-43ed-82c7-83c1684af1ee",
                            "internalId": "f7c53798-dcfa-43ed-82c7-83c1684af1ee",
                            "isSelected": false,
                            "order": 0,
                            "properties": [
                                {
                                "contentFormat": "",
                                "contentType": "ContentObject",
                                "contentValue": null,
                                "developerName": "Presenter",
                                "objectData": [
                                    {
                                    "developerName": "Person",
                                    "externalId": "2b075200-6b6c-4400-aec1-36cb7d8974fb",
                                    "internalId": "2b075200-6b6c-4400-aec1-36cb7d8974fb",
                                    "isSelected": false,
                                    "order": 0,
                                    "properties": [
                                        {
                                        "contentFormat": "",
                                        "contentType": "ContentString",
                                        "contentValue": "None",
                                        "developerName": "First Name",
                                        "objectData": null,
                                        "typeElementId": null,
                                        "typeElementPropertyId": "ce20f295-5049-446a-868f-494d8012fe4d"
                                        },
                                        {
                                        "contentFormat": "",
                                        "contentType": "ContentString",
                                        "contentValue": "Available",
                                        "developerName": "Last Name",
                                        "objectData": null,
                                        "typeElementId": null,
                                        "typeElementPropertyId": "39404b1f-0675-4347-9578-bb27cb319c61"
                                        }
                                    ],
                                    "typeElementBindingDeveloperName": null,
                                    "typeElementId": "196f8e20-806f-4c96-84b9-694ce2f2b94a"
                                    }
                                ],
                                "typeElementId": "196f8e20-806f-4c96-84b9-694ce2f2b94a",
                                "typeElementPropertyId": "a9dde048-bad9-4709-b573-09562d1b5d67"
                                },
                                {
                                "contentFormat": "",
                                "contentType": "ContentList",
                                "contentValue": null,
                                "developerName": "Subjects",
                                "objectData": [
                                    {
                                    "developerName": "Subject",
                                    "externalId": "5861cc5e-e898-46cc-a347-f1810746c0a2",
                                    "internalId": "5861cc5e-e898-46cc-a347-f1810746c0a2",
                                    "isSelected": false,
                                    "order": 0,
                                    "properties": [
                                        {
                                        "contentFormat": "",
                                        "contentType": "ContentString",
                                        "contentValue": "vacuum",
                                        "developerName": "Description",
                                        "objectData": null,
                                        "typeElementId": null,
                                        "typeElementPropertyId": "efda076a-c99f-4ff5-921a-44c866f6e0bc"
                                        },
                                        {
                                        "contentFormat": "",
                                        "contentType": "ContentString",
                                        "contentValue": "No subject",
                                        "developerName": "Name",
                                        "objectData": null,
                                        "typeElementId": null,
                                        "typeElementPropertyId": "1bb3a134-ba03-4d5d-b9b9-f12584156c94"
                                        }
                                    ],
                                    "typeElementBindingDeveloperName": null,
                                    "typeElementId": "69eaa80d-5cf6-4287-bdcd-a9f31e6eb0db"
                                    }
                                ],
                                "typeElementId": "69eaa80d-5cf6-4287-bdcd-a9f31e6eb0db",
                                "typeElementPropertyId": "fdd284aa-2074-434a-8b2b-53b0cb5dd465"
                                }
                            ],
                            "typeElementBindingDeveloperName": null,
                            "typeElementId": "84155ccd-67b7-4e3d-be14-c9fa187aea93"
                            }
                        ],
                        "typeElementId": "84155ccd-67b7-4e3d-be14-c9fa187aea93",
                        "typeElementPropertyId": "1bdd553f-0ac7-4215-9494-b7c45b277f99"
                        }
                    ],
                    "typeElementBindingDeveloperName": null,
                    "typeElementId": "5c557d42-6720-4497-af84-9665d0f837aa"
                    }
                ],
                "typeElementId": "5c557d42-6720-4497-af84-9665d0f837aa",
                "typeElementPropertyId": "1b5cbaf1-0cc9-4ad8-99ab-012ecee8a8fa"
                },
                {
                "contentFormat": "",
                "contentType": "ContentBoolean",
                "contentValue": "true",
                "developerName": "Fully Booked",
                "objectData": null,
                "typeElementId": null,
                "typeElementPropertyId": "d7bd0d71-dd0a-4191-ac18-d21008211ad3"
                },
                {
                "contentFormat": "",
                "contentType": "ContentDateTime",
                "contentValue": "2019-07-29T13:31:00+01:00",
                "developerName": "When",
                "objectData": null,
                "typeElementId": null,
                "typeElementPropertyId": "1bd83bc6-15bb-4f35-8aba-e882dd300c72"
                }
            ],
            "typeElementBindingDeveloperName": null,
            "typeElementId": "f01facfb-a9bb-4404-90ca-b0861336ae4a"
            }
        ],
        "developerName": "Training",
        "developerSummary": "",
        "elementType": "VARIABLE",
        "id": "bb887e5a-0b31-402a-a03f-9cd4a7f3d1ce",
        "initializationOperations": null,
        "isFixed": false,
        "isVersionless": false,
        "typeElementDeveloperName": "Schedule",
        "typeElementId": "f01facfb-a9bb-4404-90ca-b0861336ae4a",
        "updateByName": false,
        "whoCreated": {
            "email": "ian.haycox@dell.com",
            "firstName": "Ian",
            "id": "6c6e8404-c255-461b-9fe1-f6a6c4f1fbb0",
            "lastName": "Haycox"
        },
        "whoModified": {
            "email": "ian.haycox@dell.com",
            "firstName": "Ian",
            "id": "6c6e8404-c255-461b-9fe1-f6a6c4f1fbb0",
            "lastName": "Haycox"
        },
        "whoOwner": {
            "email": "ian.haycox@dell.com",
            "firstName": "Ian",
            "id": "6c6e8404-c255-461b-9fe1-f6a6c4f1fbb0",
            "lastName": "Haycox"
        }
    }
 *
 *
 * To re-iterate the point about sparsely populated data, this is a JS dump of a second Value, also a List of Schedule, but with NO
 * default data. See how defaultObjectData is null.
 * At any time we could get anything inbetween the populated value above and the unpopulated value below, not to forget a Type may
 * have changed properties and their names. Unnormalized is tricky to manage.
 *
    {
        "access": "PRIVATE",
        "contentFormat": "",
        "contentType": "ContentList",
        "dateCreated": "2019-10-04T12:34:29.6131439+00:00",
        "dateModified": "2019-10-04T12:34:29.6131493+00:00",
        "defaultContentValue": null,
        "defaultObjectData": null,
        "developerName": "Training no defaults",
        "developerSummary": "",
        "elementType": "VARIABLE",
        "id": "1b6ebf07-cc17-4d30-8d8f-79ad38f4bee9",
        "initializationOperations": null,
        "isFixed": false,
        "isVersionless": false,
        "typeElementDeveloperName": "Schedule",
        "typeElementId": "f01facfb-a9bb-4404-90ca-b0861336ae4a",
        "updateByName": false,
        "whoCreated": {
            "email": "ian.haycox@dell.com",
            "firstName": "Ian",
            "id": "6c6e8404-c255-461b-9fe1-f6a6c4f1fbb0",
            "lastName": "Haycox"
        },
        "whoModified": {
            "email": "ian.haycox@dell.com",
            "firstName": "Ian",
            "id": "6c6e8404-c255-461b-9fe1-f6a6c4f1fbb0",
            "lastName": "Haycox"
        },
        "whoOwner": {
            "email": "ian.haycox@dell.com",
            "firstName": "Ian",
            "id": "6c6e8404-c255-461b-9fe1-f6a6c4f1fbb0",
            "lastName": "Haycox"
        }
    }
 *
 * If you made it this far, then congratulations.
 *
 * Now it's time to point out the GOTCHA that within the hierachy only the top-level default data is stored in the defaultObjectData property.
 * You'll notice above, that in the children, grand-children, ... the defaultObjectData property is called objectData  For this reason to
 * make the algorithms consistent and avoid special cases, upon fetaching the Value from the API we rename defaultObjectData to objectData
 * at the top-level, and then rename it back on save.
 */

import actionTypes from '../actions/reduxActions/actionTypes';
import { VALUE_TYPES } from '../components/elementSelector/selectors/picker/valuePickerUtils';
import { ACCESS_LEVELS } from '../constants';
import { assocPath, clone } from 'ramda';
import Model from '../utils/model';

const initialState = {
    isLoading: 0, // Counter for concurrent loading. Zero all done.
    isSaving: false,
    isDuplicateWarning: false,
    value: {
        access: ACCESS_LEVELS.PRIVATE.value,
        contentFormat: null,
        contentType: null,
        defaultContentValue: null,
        valueEditorId: Model.guid(), // Used to track which 'chunk' of objectData we are displaying for the Breadcrumb
        objectData: null, // IMPORTANT - this is actually defaultObjectData - the name is renamed on fetch() and save()
        developerName: '',
        developerSummary: '',
        elementType: 'VARIABLE',
        id: null,
        initializationOperations: null,
        isFixed: false,
        isVersionless: false,
        typeElementDeveloperName: null,
        typeElementId: null,
    },
    filteredProperties: {}, // Subset of properties to filter, indexed by typeElementId, e.g. {'a-a-a-a-a': ['prop1','prop2'], ...}
    breadcrumbTrail: [],
    breadcrumbActiveId: null,
};

/**
 * Find a property in the value object tree with a matching valueEditorId.
 *
 * We use the valueEditorId to locate the appropriate place in the Value object structure.
 * Typically the Value object looks like this, abbreviated,
 *
 * { contentType
 *   developerName
 *   contentValue
 *   valueEditorId    ff876145-ed27-d119-b696-c7c8b99c71aa
 *   objectData [
 *     {
 *       developerName
 *       contentValue
 *       internalId    95876145-ed27-d119-b696-c7c8b99c718b
 *       properties [
 *         {
 *            developerName
 *            contentValue
 *            typeElementPropertyId
 *            valueEditorId    ee876145-ed27-d119-b696-c7c8b99c71bb
 *            objectData [
 *               developerName
 *               contentValue
 *               internalId     5c557d42-6720-4497-af84-9665d0f837aa
 *               properties [
 *                 {
 *                   developerName
 *                   contentValue
 *                   valueEditorId    cc876145-ed27-d119-b696-c7c8b99c71cc
 *                   objectData: null
 *                 }
 *               ]
 *            ]
 *         }
 *       ]
 *     }
 *   ]
 * }
 *
 * So for example to locate a property at the bottom of the tree above we would:
 *
 *     propRef = locateProperty(state.value, 'cc876145-ed27-d119-b696-c7c8b99c71cc');
 *
 * The return value points to index in the property array, e.g. propRef.contentValue = "foo" would update the contentValue
 * at the bottom of the above tree.
 *
 * @param {Object} value The current recursed value to traverse
 * @param {String} valueEditorId
 * @return {Reference} a reference to the found property, or null if not found
 */
export const locateProperty = (value, valueEditorId) => {

    if (!valueEditorId) {
        return value;
    }

    // Recurse down the value hierarchy looking for a matching valueEditorId
    const locateEditorId = (val, editorId) => {
        if (val.valueEditorId === editorId) {
            return val;
        }
        if (val.objectData) {

            // eslint-disable-next-line no-restricted-syntax
            for (const objectData of val.objectData) {
                // eslint-disable-next-line no-restricted-syntax
                for (const property of objectData.properties) {
                    const p = locateEditorId(property, editorId);
                    if (p) {
                        return p;
                    }
                }
            }
        }

        return null;
    };

    const valueRef = locateEditorId(value, valueEditorId);

    return valueRef;
};

/**
 * Update a top level field in the Value object
 *
 * @param {Object} state the current state
 * @param {Object} target form input element
 * @param {String} fieldName name of field to update
 */
const updateValueField = (state, target, fieldName) => {
    const contentValue = target.type === 'checkbox' ? target.checked : target.value;
    return assocPath([fieldName], contentValue, state.value);
};

const valueEditor = (state = initialState, { type, payload }) => {

    switch (type) {

    case actionTypes.VALUE_EDITOR_LOADING:
        return {
            ...state,
            isLoading: state.isLoading + 1,
        };

    case actionTypes.VALUE_EDITOR_LOADED:
        return {
            ...state,
            isLoading: Math.max(state.isLoading - 1, 0),
        };

    case actionTypes.VALUE_EDITOR_SAVING:
        return {
            ...state,
            isSaving: true,
        };

    case actionTypes.VALUE_EDITOR_SAVED:
        return {
            ...state,
            isSaving: false,
        };

    case actionTypes.VALUE_EDITOR_SET_DUPLICATE_WARNING:
        return {
            ...state,
            isDuplicateWarning: payload,
        };

    case actionTypes.VALUE_EDITOR_SET_VALUE: {

        const { value, typeElements } = payload;
        const newValue = clone(value || initialState.value);

        // It gets a bit ugly here, because the backend API does not sanitize existing
        // Values if/when a Type definition changes.
        //
        // For example, if a Value was created with a Type that has
        // a couple of properties, e.g. `p1` and `p2` and then the Type is changed
        // to remove `p1` and add `p3` we need to fix up any default property values
        // in the fetched Value to reflect the new Type definition.
        //
        // Bit of a naive algorithm, but we want the intersection of two sets.
        //
        const alignTypeProperties = (val) => {
            if (val.objectData) {
                val.objectData.forEach((od) => {

                    // Remove properties whose type no longer exists
                    const typeDefinition = typeElements.find(types => types.id === od.typeElementId);
                    od.properties = od.properties.filter(p => typeDefinition.properties
                        .find(typeProperty => typeProperty.id === p.typeElementPropertyId));

                    // Now Add missing properties to the Value
                    typeDefinition.properties.forEach((typeProperty) => {

                        if (!od.properties.find(p => typeProperty.id === p.typeElementPropertyId)) {
                            od.properties.push({
                                contentFormat: '',
                                contentType: typeProperty.contentType,
                                contentValue: null,
                                valueEditorId: Model.guid(),
                                developerName: typeProperty.developerName,
                                objectData: null,
                                typeElementId: typeProperty.typeElementId,
                                typeElementPropertyId: typeProperty.id,
                            });
                        }
                    });
                });

                val.objectData.map(od => od.properties.map(p => alignTypeProperties(p)));
            }

            return val;
        };

        const newValue2 = alignTypeProperties(newValue);

        return {
            ...state,
            isLoading: Math.max(state.isLoading - 1, 0),
            isSaving: false,
            value: newValue2,
            breadcrumbTrail: [],
            breadcrumbActiveId: null,
        };
    }

    case actionTypes.VALUE_EDITOR_SET_VALUE_DEVELOPER_NAME: {

        return {
            ...state,
            value: updateValueField(state, payload, 'developerName'),
        };
    }

    case actionTypes.VALUE_EDITOR_SET_VALUE_CONTENT_TYPE: {

        if (!VALUE_TYPES.find(valueType => valueType.value === payload.value)) {
            // Don't allow an unknown Content Type
            return state;
        }

        const value = assocPath(['contentType'], payload.value, state.value);
        // Changing content type invalidates any prior default values and formatting options
        value.contentFormat = null;
        value.defaultContentValue = null;
        value.objectData = null;
        // and the Type of the Value
        value.typeElementId = null;

        return {
            ...state,
            value,
            breadcrumbTrail: [], // Reset breadcrumb trail when changing Content Type
            breadcrumbActiveId: null,
        };
    }

    case actionTypes.VALUE_EDITOR_SET_VALUE_TYPE_ELEMENT_ID: {
        const value = assocPath(['typeElementId'], payload.value, state.value);
        // Changing Value Type invalidates any prior default object data
        value.objectData = null;
        return {
            ...state,
            value,
            breadcrumbTrail: [], // Reset breadcrumb trail when changing Type
            breadcrumbActiveId: null,
        };
    }

    case actionTypes.VALUE_EDITOR_SET_VALUE_DEFAULT_CONTENT_VALUE:

        if (VALUE_TYPES.find(valueType => valueType.value === state.value.contentType && valueType.requiresType)) {
            // Default content value is only applicable for primitive types, ContentString, ...
            return state;
        }

        return {
            ...state,
            value: updateValueField(state, payload, 'defaultContentValue'),
        };

    case actionTypes.VALUE_EDITOR_SET_VALUE_CONTENT_FORMAT:

        if (!VALUE_TYPES.find(valueType => valueType.value === state.value.contentType && valueType.canBeFormatted)) {
            // Formatting is only applicable for some primitive types
            return state;
        }

        return {
            ...state,
            value: updateValueField(state, payload, 'contentFormat'),
        };

    case actionTypes.VALUE_EDITOR_SET_VALUE_IS_FIXED:
        return {
            ...state,
            value: updateValueField(state, payload, 'isFixed'),
        };

    case actionTypes.VALUE_EDITOR_SET_VALUE_ACCESS:
        return {
            ...state,
            value: assocPath(['access'], payload.value, state.value),
        };

    case actionTypes.VALUE_EDITOR_SET_VALUE_IS_VERSIONLESS:
        return {
            ...state,
            value: updateValueField(state, payload, 'isVersionless'),
        };

    case actionTypes.VALUE_EDITOR_SET_VALUE_DEVELOPER_SUMMARY:
        return {
            ...state,
            value: updateValueField(state, payload, 'developerSummary'),
        };

    case actionTypes.VALUE_EDITOR_SET_VALUE_OBJECT_DATA: {
        // Add new objectData within the tree of objects

        const { objectData, valueEditorId } = payload;
        const newValue = clone(state.value);
        let { breadcrumbActiveId } = state;

        // propertyRef is a Javascript reference to a properties array element. All modifications
        // should be made in-place.
        const propertyRef = locateProperty(newValue, valueEditorId);
        if (propertyRef) {
            if (propertyRef.objectData) {
                propertyRef.objectData.push(objectData);
            } else {
                propertyRef.objectData = [objectData];
            }
            breadcrumbActiveId = valueEditorId;
        }

        return {
            ...state,
            value: newValue,
            breadcrumbActiveId,
        };
    }

    case actionTypes.VALUE_EDITOR_SET_VALUE_OBJECT_DATA_CONTENT_VALUE: {
        // Update the default value for the objectData located by the valueEditorId

        const { target, valueEditorId } = payload;
        const newValue = clone(state.value);
        const contentValue = target.type === 'checkbox' ? target.checked : target.value;

        // propertyRef is a Javascript reference to a properties array element. All modifications
        // should be made in-place.
        const propertyRef = locateProperty(newValue, valueEditorId);
        if (propertyRef) {
            propertyRef.contentValue = contentValue;
        }

        return {
            ...state,
            value: newValue,
        };
    }

    case actionTypes.VALUE_EDITOR_DELETE_OBJECT_DATA: {

        // We want to delete the objectData with a specified internalId, so we need to locate the ancestor
        // object using the valueEditorId to then find the matching internalId

        const { valueEditorId, internalId } = payload;

        const newValue = clone(state.value);

        const breadcrumbTrail = clone(state.breadcrumbTrail);
        let { breadcrumbActiveId } = state;

        // propertyRef is a reference to the index in the properties array with the matching valueEditorId to delete.
        // Careful how you maniplulate propRef as it's a reference and changes should be done in-place.
        const propertyRef = locateProperty(newValue, valueEditorId);
        if (!propertyRef) {
            return state;
        }

        const idx = propertyRef.objectData.findIndex(od => od.internalId === internalId);
        if (idx !== -1) {
            propertyRef.objectData.splice(idx, 1);
            if (propertyRef.objectData.length === 0) {
                propertyRef.objectData = null;
                // Last item, so back to the previous breadcrumb
                breadcrumbTrail.pop();
                breadcrumbActiveId = breadcrumbTrail.length === 0 ? null : breadcrumbTrail.slice(-1)[0].id;
            }
        }

        return {
            ...state,
            value: newValue,
            breadcrumbTrail,
            breadcrumbActiveId,
        };
    }

    case actionTypes.VALUE_EDITOR_SET_PROPERTY_FILTER: {
        // List of filtered Type properties indexed by the type. This is so we can keep multiple
        // instances of filtered properties for each type as we navigate between nested types and
        // remember previous selections.

        const { typeElementId, selectedProperties } = payload;
        if (!typeElementId || !Array.isArray(selectedProperties)) {
            return state;
        }

        const filters = clone(state.filteredProperties);
        filters[typeElementId] = selectedProperties;
        return {
            ...state,
            filteredProperties: filters,
        };
    }

    case actionTypes.VALUE_EDITOR_PUSH_BREADCRUMB: {

        const { valueEditorId, typeName, onClick } = payload;
        const breadcrumbTrail = clone(state.breadcrumbTrail);
        breadcrumbTrail.push({ id: valueEditorId, content: typeName, onClick });
        return {
            ...state,
            breadcrumbTrail,
            breadcrumbActiveId: valueEditorId,
        };
    }

    case actionTypes.VALUE_EDITOR_POP_BREADCRUMB: {

        const breadcrumbTrail = clone(state.breadcrumbTrail);
        breadcrumbTrail.pop();
        return {
            ...state,
            breadcrumbTrail,
            breadcrumbActiveId: breadcrumbTrail.length === 0 ? null : breadcrumbTrail.slice(-1)[0].id,
        };
    }

    case actionTypes.VALUE_EDITOR_SET_ACTIVE_BREADCRUMB: {

        const { breadcrumbActiveId } = payload;

        const breadcrumbTrail = clone(state.breadcrumbTrail);

        const newCrumb = breadcrumbTrail.findIndex(crumb => crumb.id === breadcrumbActiveId);
        if (newCrumb !== -1) {
            return {
                ...state,
                breadcrumbTrail: breadcrumbTrail.slice(0, newCrumb + 1),
                breadcrumbActiveId,
            };
        }

        return state;
    }

    default:
        return state;
    }
};

export default valueEditor;
